// Route render ảnh share TRẦN — dùng trong iframe ẩn (rộng cố định 1280px) để
// chụp board React THẬT ở layout desktop, đồng nhất mọi thiết bị. Sau khi chụp
// xong, postMessage blob về parent (share-dialog). Không có nav/CTA (xem __root).
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { TuviLaSoBoard } from "@/components/tuvi/tuvi-la-so-board";
import { BaziBoard } from "@/components/bazi/bazi-board";
import { HexagramFullBoard } from "@/components/iching/hexagram-full-board";
import {
  getSharedResource,
  type SharedResource,
  type TuviChart,
  type BaziChart,
} from "@/lib/boide-api";
import { composeShareImage } from "@/lib/share-image";

const FONT_CSS_URL =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Inter:wght@400;500;600&family=Be+Vietnam+Pro:wght@400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap&subset=vietnamese";

type RenderLoaderData = { resource: SharedResource | null };

export const Route = createFileRoute("/render/$token")({
  loader: async ({ params }): Promise<RenderLoaderData> => {
    try {
      return { resource: await getSharedResource(params.token) };
    } catch {
      return { resource: null };
    }
  },
  component: RenderPage,
});

/** Ép webfont tải NGAY (route thường trì hoãn tới sau FCP) để chụp đúng chữ. */
function ensureFonts(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  if (!document.querySelector("link[data-share-font]")) {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONT_CSS_URL;
    l.setAttribute("data-share-font", "1");
    document.head.appendChild(l);
  }
  const fonts = (document as unknown as { fonts?: { ready: Promise<unknown> } }).fonts;
  return fonts?.ready
    ? fonts.ready.then(() => undefined).catch(() => undefined)
    : Promise.resolve();
}

function RenderPage() {
  const { resource } = Route.useLoaderData();
  const boardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Theme sáng/tối theo ?theme (composeShareImage đọc class "dark" chọn nền).
    const theme =
      new URLSearchParams(window.location.search).get("theme") === "dark" ? "dark" : "light";
    const root = document.documentElement;
    root.classList.remove("dark", "light");
    root.classList.add(theme);

    const post = (msg: Record<string, unknown>) => window.parent?.postMessage(msg, "*");

    if (!resource) {
      post({ type: "boide-share-image", ok: false, error: "no-resource" });
      return;
    }

    let cancelled = false;
    (async () => {
      await ensureFonts();
      await new Promise((r) => setTimeout(r, 500)); // để layout/animation settle
      if (cancelled) return;
      const node = boardRef.current;
      if (!node) return;
      try {
        const blob = await composeShareImage(node);
        if (cancelled) return;
        post({ type: "boide-share-image", ok: true, blob });
      } catch (e) {
        post({ type: "boide-share-image", ok: false, error: String(e) });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resource]);

  if (!resource) return <div />;

  return (
    <div style={{ width: 1200, padding: 12, background: "transparent" }}>
      <div ref={boardRef} style={{ width: 1200 }}>
        {resource.resource_type === "tuvi" ? (
          <TuviLaSoBoard apiChart={resource.data as TuviChart} maskPii={resource.mask_pii} />
        ) : resource.resource_type === "bazi" ? (
          <BaziBoard chart={resource.data as BaziChart} maskPii={resource.mask_pii} />
        ) : (
          <HexagramFullBoard hex={resource.data.hexagram_result} />
        )}
      </div>
    </div>
  );
}
